import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const features = {
  'custom-software': {
    title: 'Custom Software Development',
    subtitle: 'Tailored solutions for your unique business needs',
    description: 'We design and develop custom software solutions that perfectly align with your business processes and goals. Our team works closely with you to understand your requirements and deliver software that drives efficiency and growth.',
    benefits: [
      'Fully customized to your specific requirements',
      'Scalable architecture for future growth',
      'Integration with existing systems',
      'Modern technology stack',
      'Comprehensive documentation',
      'Ongoing support and maintenance'
    ],
    process: [
      { step: 1, title: 'Discovery', description: 'We analyze your requirements and business processes' },
      { step: 2, title: 'Design', description: 'Create detailed technical specifications and UI/UX designs' },
      { step: 3, title: 'Development', description: 'Build your solution using agile methodologies' },
      { step: 4, title: 'Testing', description: 'Comprehensive QA to ensure quality and reliability' },
      { step: 5, title: 'Deployment', description: 'Smooth deployment and knowledge transfer' },
      { step: 6, title: 'Support', description: 'Ongoing maintenance and continuous improvement' }
    ]
  },
  'qa-automation': {
    title: 'QA Automation',
    subtitle: 'Ensure quality at every stage of development',
    description: 'Our QA automation services help you maintain high quality standards while accelerating your development cycles. We implement comprehensive testing strategies that catch issues early and ensure reliable releases.',
    benefits: [
      'Faster release cycles',
      'Reduced manual testing effort',
      'Consistent test coverage',
      'Early bug detection',
      'CI/CD integration',
      'Detailed reporting and analytics'
    ],
    process: [
      { step: 1, title: 'Assessment', description: 'Evaluate current testing processes and identify gaps' },
      { step: 2, title: 'Strategy', description: 'Define automation framework and tool selection' },
      { step: 3, title: 'Implementation', description: 'Build and configure automation infrastructure' },
      { step: 4, title: 'Test Creation', description: 'Develop automated test suites' },
      { step: 5, title: 'Integration', description: 'Integrate with CI/CD pipelines' },
      { step: 6, title: 'Optimization', description: 'Continuous improvement and maintenance' }
    ]
  },
  'cloud-solutions': {
    title: 'Cloud Solutions',
    subtitle: 'Scale your infrastructure with confidence',
    description: 'We help you leverage the power of cloud computing to build scalable, reliable, and cost-effective infrastructure. Our cloud experts guide you through migration, optimization, and management of cloud resources.',
    benefits: [
      'Reduced infrastructure costs',
      'Improved scalability and flexibility',
      'Enhanced security and compliance',
      'High availability and disaster recovery',
      'Global reach and performance',
      'Pay-as-you-go pricing models'
    ],
    process: [
      { step: 1, title: 'Assessment', description: 'Evaluate current infrastructure and requirements' },
      { step: 2, title: 'Planning', description: 'Design cloud architecture and migration strategy' },
      { step: 3, title: 'Migration', description: 'Execute migration with minimal downtime' },
      { step: 4, title: 'Optimization', description: 'Fine-tune for performance and cost efficiency' },
      { step: 5, title: 'Security', description: 'Implement security best practices' },
      { step: 6, title: 'Management', description: 'Ongoing monitoring and support' }
    ]
  },
  'devops': {
    title: 'DevOps Implementation',
    subtitle: 'Streamline your development and operations',
    description: 'Transform your software delivery with modern DevOps practices. We help you build automated pipelines, implement infrastructure as code, and create a culture of continuous improvement.',
    benefits: [
      'Faster time to market',
      'Improved collaboration',
      'Automated deployments',
      'Infrastructure as code',
      'Continuous monitoring',
      'Reduced operational overhead'
    ],
    process: [
      { step: 1, title: 'Assessment', description: 'Analyze current development and deployment processes' },
      { step: 2, title: 'Strategy', description: 'Define DevOps roadmap and toolchain' },
      { step: 3, title: 'Implementation', description: 'Set up CI/CD pipelines and infrastructure' },
      { step: 4, title: 'Automation', description: 'Automate builds, tests, and deployments' },
      { step: 5, title: 'Monitoring', description: 'Implement observability and alerting' },
      { step: 6, title: 'Culture', description: 'Foster DevOps culture and practices' }
    ]
  }
};

function FeatureDetail() {
  const { featureId } = useParams();
  const feature = features[featureId];

  if (!feature) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-text-primary mb-4">Feature not found</h1>
          <Link to="/" className="text-accent hover:underline">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary">
      <Navigation scrolled={true} />
      
      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Back link */}
          <Link 
            to="/#services" 
            className="inline-flex items-center text-accent hover:text-accent/80 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Services
          </Link>

          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              {feature.title}
            </h1>
            <p className="text-xl text-accent">{feature.subtitle}</p>
          </div>

          {/* Description */}
          <div className="mb-12">
            <p className="text-lg text-text-secondary leading-relaxed max-w-3xl">
              {feature.description}
            </p>
          </div>

          {/* Benefits */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-text-primary mb-6">Key Benefits</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {feature.benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-secondary/30 rounded-lg border border-accent/10"
                >
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-text-secondary">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-text-primary mb-6">Our Process</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {feature.process.map((item) => (
                <div 
                  key={item.step}
                  className="p-6 bg-secondary/30 rounded-lg border border-accent/10 hover:border-accent/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                    <span className="text-accent font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">{item.title}</h3>
                  <p className="text-text-secondary text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-secondary/30 rounded-xl p-8 border border-accent/20">
            <h2 className="text-2xl font-bold text-text-primary mb-4">
              Ready to get started?
            </h2>
            <p className="text-text-secondary mb-6 max-w-xl mx-auto">
              Let's discuss how we can help you achieve your goals with our {feature.title.toLowerCase()} expertise.
            </p>
            <Link
              to="/#contact"
              className="inline-block px-8 py-3 bg-accent text-primary font-semibold rounded-lg hover:bg-accent/90 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default FeatureDetail;
